import React, {useState, useRef, useEffect} from 'react';

export function DualRangeSlider({onChange, min, max}) {
  const [minValue, setMinValue] = useState(min || 10);
  const [maxValue, setMaxValue] = useState(max || 78);

  const rangeRef = useRef(null);
  const minValueRef = useRef(null);
  const maxValueRef = useRef(null);

  useEffect(() => {
    if (!rangeRef.current || !minValueRef.current || !maxValueRef.current)
      return;

    const min = 0;
    const max = 100;
    const range = max - min;

    const minPercentage = ((minValue - min) / range) * 100;
    const maxPercentage = ((maxValue - min) / range) * 100;

    minValueRef.current.style.left = `${minPercentage}%`;
    maxValueRef.current.style.left = `${maxPercentage}%`;
    rangeRef.current.style.background = `linear-gradient(
      to right,
      #dbeafe 0%,
      #dbeafe ${minPercentage}%,
      var(--color-primary) ${minPercentage}%,
      var(--color-primary) ${maxPercentage}%,
      #dbeafe ${maxPercentage}%,
      #dbeafe 100%
    )`;
  }, [minValue, maxValue]);

  const afterSudo = `
  absolute transform -translate-x-1/2 bg-[var(--color-primary)] text-white rounded-md px-2 py-0.5 text-sm font-medium
  after:h-3 
  after:w-3 
  after:content-[''] 
  after:absolute 
  after:bg-[var(--color-primary)] 
  after:top-3 
  after:left-1/3 
  after:rotate-45 
  after:-z-10
  `;

  const handleDrag = (e, isMin) => {
    if (!rangeRef.current) return;

    const rect = rangeRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percentage = Math.min(
      100,
      Math.max(0, Math.round((offsetX / rect.width) * 100)),
    );

    if (isMin) {
      setMinValue(Math.min(percentage, maxValue - 1));
      onChange('min', Math.min(percentage, maxValue - 1));
    } else {
      setMaxValue(Math.max(percentage, minValue + 1));
      onChange('max', Math.max(percentage, minValue + 1));
    }
  };

  return (
    <div className="w-full max-w-3xl mx-auto px-4 rounded-full relative">
      <div className="flex justify-between mb-2 text-gray-700 relative h-6">
        <span ref={minValueRef} className={`${afterSudo}`}>
          {minValue}
        </span>

        <span ref={maxValueRef} className={`${afterSudo}`}>
          {maxValue > 100 ? 100 : maxValue}
        </span>
      </div>

      <div className="relative h-2 top-[3px] rounded-full" ref={rangeRef}>
        <div
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-[var(--color-primary)] rounded-full cursor-pointer shadow-sm"
          style={{
            left: `${minValue}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: 20,
            background: minValue === 0 ? '#D2D2D2' : 'white',
            borderColor: minValue === 0 ? '#D2D2D2' : 'var(--color-primary)',
          }}
          onMouseDown={e => {
            e.preventDefault();

            const handleMouseMove = moveEvent => {
              handleDrag(moveEvent, true);
            };

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />

        <div
          className="absolute top-1/2 -translate-y-1/2 w-5 h-5 border-2 border-[var(--color-primary)] rounded-full cursor-pointer shadow-sm"
          style={{
            left: `${maxValue > 100 ? 100 : maxValue}%`,
            transform: 'translate(-50%, -50%)',
            zIndex: 30,
            background: maxValue === 100 ? 'var(--color-primary)' : 'white',
            borderColor:
              maxValue === 100
                ? 'var(--color-primary)'
                : 'var(--color-primary)',
          }}
          onMouseDown={e => {
            e.preventDefault();

            const handleMouseMove = moveEvent => {
              handleDrag(moveEvent, false);
            };

            const handleMouseUp = () => {
              document.removeEventListener('mousemove', handleMouseMove);
              document.removeEventListener('mouseup', handleMouseUp);
            };

            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
          }}
        />
      </div>
    </div>
  );
}
